import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";

const API = '/api';

export default function AbrechnungenList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [abrechnungen, setAbrechnungen] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !user?.permissions?.can_manage_users) {
      navigate("/app");
      toast.error("Nur Admins");
    }
  }, [user, navigate]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await authFetch(`${API}/abrechnungen`);
      if (res.ok) setAbrechnungen(await res.json());
    } catch { toast.error("Fehler"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!confirm("Abrechnung löschen?")) return;
    try {
      const res = await authFetch(`${API}/abrechnungen/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Gelöscht"); loadData(); }
      else { const er = await res.json(); toast.error(er.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
  };

  if (loading) return <div className="py-4 text-[12px] text-gray-400">Lade…</div>;

  return (
    <div data-testid="abrechnungen-list">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-[15px] font-semibold text-gray-800">Abrechnungen</h2>
          <span className="text-[11px] text-gray-400">{abrechnungen.length} Einträge</span>
        </div>
        <button
          onClick={() => navigate("/app/stundenkonten")}
          className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-medium bg-slate-800 text-white hover:bg-slate-900 rounded-sm"
          data-testid="new-abrechnung-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Neue Abrechnung
        </button>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden" style={{ borderRadius: 2 }}>
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f1f3f5", borderBottom: "1px solid #dce0e5" }}>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed" }}>Status</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed" }}>Projekt</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed" }}>Zeitraum</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed", width: 80 }}>Mitarb.</th>
              <th className="text-right text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed", width: 70 }}>Stunden</th>
              <th className="text-right text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed", width: 100 }}>Summe</th>
              <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-1.5 text-gray-500" style={{ borderRight: "1px solid #e8eaed", width: 80 }}>Erstellt</th>
              <th className="text-[11px] px-2 py-1.5" style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {abrechnungen.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-[12px] text-gray-400">Keine Abrechnungen vorhanden</td></tr>
            ) : (
              abrechnungen.map((a, i) => (
                <tr
                  key={a.id}
                  className="cursor-pointer"
                  style={{ borderBottom: "1px solid var(--tga-border)" }}
                  onClick={() => navigate(`/app/abrechnungen/${a.id}`)}
                  data-testid={`abrechnung-item-${a.id}`}
                >
                  <td className="px-2 text-[12px] font-medium" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: a.status === "Exportiert" ? "var(--tga-green)" : "var(--tga-amber)" }}>{a.status}</td>
                  <td className="px-2 text-[13px] font-medium" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-bright)" }}>{a.projekt_name}</td>
                  <td className="px-2 text-[12px]" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>{a.zeitraum_von} – {a.zeitraum_bis}</td>
                  <td className="px-2 text-[12px] text-center" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>{a.personal_anzahl}</td>
                  <td className="px-2 text-[12px] text-right font-mono" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}>{a.gesamtstunden?.toFixed(1)}h</td>
                  <td className="px-2 text-[13px] text-right font-medium" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-bright)" }}>{a.gesamtsumme?.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</td>
                  <td className="px-2 text-[11px]" style={{ height: 36, borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-muted)" }}>{a.erstellt_am ? format(new Date(a.erstellt_am), "dd.MM.yy", { locale: de }) : "—"}</td>
                  <td className="px-2 text-center" style={{ height: 36 }}>
                    {a.status === "Entwurf" && (
                      <button onClick={(e) => handleDelete(a.id, e)} className="text-red-400 hover:text-red-600" data-testid={`delete-abrechnung-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
